import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';

interface Keyword {
    id: number;
    keyword: string;
    count: number;
    clause: string;
    isEditing?: boolean;
    isExcluded?: boolean;
}

interface KeywordsContextType {
    keywords: Keyword[];
    setKeywords: (keywords: Keyword[]) => void;
    excludedKeywords: Keyword[];
    setExcludedKeywords: (keywords: Keyword[]) => void;
}

const defaultContextValue: KeywordsContextType = {
    keywords: [],
    setKeywords: () => ({ }),
    excludedKeywords: [],
    setExcludedKeywords: () => ({ })
};

const KeywordsContext = createContext<KeywordsContextType>(defaultContextValue);

export const useKeywordsContext = () => useContext(KeywordsContext);

interface KeywordsProviderProps {
    children: ReactNode;
}

export const initialKeywords = [
    { id: 1, keyword: '検査', count: 0, clause: '', isExcluded: false },
    { id: 2, keyword: '点検', count: 0, clause: '', isExcluded: false },
    { id: 3, keyword: '調査', count: 0, clause: '', isExcluded: false },
    { id: 4, keyword: '目視', count: 0, clause: '', isExcluded: false },
    { id: 5, keyword: '実地', count: 0, clause: '', isExcluded: false },
    { id: 6, keyword: '立ち入', count: 0, clause: '', isExcluded: false },
    { id: 7, keyword: '立入', count: 0, clause: '', isExcluded: false },
    { id: 8, keyword: '現地', count: 0, clause: '', isExcluded: false },
    { id: 9, keyword: '訪問', count: 0, clause: '', isExcluded: false },
    { id: 10, keyword: '巡視', count: 0, clause: '', isExcluded: false },
    { id: 11, keyword: '見張', count: 0, clause: '', isExcluded: false },
    { id: 12, keyword: '実地', count: 0, clause: '', isExcluded: false },
    { id: 13, keyword: '監査', count: 0, clause: '', isExcluded: false },
    { id: 14, keyword: '測定', count: 0, clause: '', isExcluded: false },
    { id: 15, keyword: '査察', count: 0, clause: '', isExcluded: false },
    { id: 16, keyword: '確認', count: 0, clause: '', isExcluded: false },
    { id: 17, keyword: '審査', count: 0, clause: '', isExcluded: false },
    { id: 18, keyword: '定期', count: 0, clause: '', isExcluded: false },
    { id: 19, keyword: '年一回', count: 0, clause: '', isExcluded: false },
    { id: 20, keyword: '月一回', count: 0, clause: '', isExcluded: false },
    { id: 21, keyword: '日一回', count: 0, clause: '', isExcluded: false },
    { id: 22, keyword: '週一回', count: 0, clause: '', isExcluded: false },
    { id: 23, keyword: '常駐', count: 0, clause: '', isExcluded: false },
    { id: 24, keyword: '専任', count: 0, clause: '', isExcluded: false },
    { id: 25, keyword: '選任', count: 0, clause: '', isExcluded: false },
    { id: 26, keyword: '専ら', count: 0, clause: '', isExcluded: false },
    { id: 27, keyword: '常勤', count: 0, clause: '', isExcluded: false },
    { id: 28, keyword: '配置', count: 0, clause: '', isExcluded: false },
    { id: 29, keyword: '対面講習', count: 0, clause: '', isExcluded: false },
    { id: 30, keyword: '講習', count: 0, clause: '', isExcluded: false },
    { id: 31, keyword: '研修', count: 0, clause: '', isExcluded: false },
    { id: 32, keyword: '掲示', count: 0, clause: '', isExcluded: false },
    { id: 33, keyword: '掲げ', count: 0, clause: '', isExcluded: false },
    { id: 34, keyword: '閲覧', count: 0, clause: '', isExcluded: false },
    { id: 35, keyword: '縦覧', count: 0, clause: '', isExcluded: false },
    { id: 36, keyword: '光ディスク', count: 0, clause: '', isExcluded: false },
    { id: 37, keyword: 'シー・ディー・ロム', count: 0, clause: '', isExcluded: false },
    { id: 38, keyword: 'シーディーロム', count: 0, clause: '', isExcluded: false },
    { id: 39, keyword: '磁気ディスク', count: 0, clause: '', isExcluded: false },
    { id: 40, keyword: 'フレキシブルディスク', count: 0, clause: '', isExcluded: false },
    { id: 41, keyword: '磁気テープ', count: 0, clause: '', isExcluded: false },
];

export const initialExcludedKeywords = [
    { id: 1, keyword: '条に掲げる', count: 0, clause: '', isExcluded: true },
    { id: 2, keyword: '項に掲げる', count: 0, clause: '', isExcluded: true },
    { id: 3, keyword: '号に掲げる', count: 0, clause: '', isExcluded: true },
    { id: 4, keyword: 'イに掲げる', count: 0, clause: '', isExcluded: true },
    { id: 5, keyword: 'ロに掲げる', count: 0, clause: '', isExcluded: true },
    { id: 6, keyword: 'ハに掲げる', count: 0, clause: '', isExcluded: true },
    { id: 7, keyword: 'ただし書に掲げる', count: 0, clause: '', isExcluded: true },
    { id: 8, keyword: '規定に掲げる', count: 0, clause: '', isExcluded: true },
    { id: 9, keyword: 'までに掲げる', count: 0, clause: '', isExcluded: true },
    { id: 10, keyword: '表に掲げる', count: 0, clause: '', isExcluded: true },
    { id: 11, keyword: '欄に掲げる', count: 0, clause: '', isExcluded: true },
    { id: 12, keyword: '次に掲げる', count: 0, clause: '', isExcluded: true },
    { id: 13, keyword: 'いずれかに掲げる', count: 0, clause: '', isExcluded: true },
    { id: 14, keyword: '左に掲げる', count: 0, clause: '', isExcluded: true },
    { id: 15, keyword: '一に掲げる', count: 0, clause: '', isExcluded: true },
    { id: 16, keyword: '二に掲げる', count: 0, clause: '', isExcluded: true },
    { id: 17, keyword: '三に掲げる', count: 0, clause: '', isExcluded: true },
    { id: 18, keyword: '四に掲げる', count: 0, clause: '', isExcluded: true },
    { id: 19, keyword: '五に掲げる', count: 0, clause: '', isExcluded: true },
    { id: 20, keyword: '六に掲げる', count: 0, clause: '', isExcluded: true },
    { id: 21, keyword: '七に掲げる', count: 0, clause: '', isExcluded: true },
    { id: 22, keyword: '八に掲げる', count: 0, clause: '', isExcluded: true },
    { id: 23, keyword: '九に掲げる', count: 0, clause: '', isExcluded: true },
    { id: 24, keyword: '十に掲げる', count: 0, clause: '', isExcluded: true },
];

const loadInitialKeywords = (key: string, initialKeywords: Keyword[], setKeywords: (keywords: Keyword[]) => void) => {
    const storedKeywords = localStorage.getItem(key);
    if (storedKeywords) {
        const parsedKeywords = JSON.parse(storedKeywords);
        /*
        if (parsedKeywords.length === 0) {
            setKeywords(initialKeywords);
            localStorage.setItem(key, JSON.stringify(initialKeywords));
        } else {
            setKeywords(parsedKeywords);
        }
        */
        // save localStorage
        setKeywords(parsedKeywords);
    } else {
        setKeywords(initialKeywords);
        localStorage.setItem(key, JSON.stringify(initialKeywords));
    }
};

export const KeywordsProvider: React.FC<KeywordsProviderProps> = ({ children }) => {
    const [keywords, setKeywords] = useState<Keyword[]>([]);
    const [excludedKeywords, setExcludedKeywords] = useState<Keyword[]>([]);

    useEffect(() => {
        loadInitialKeywords('keywords', initialKeywords, setKeywords);
        loadInitialKeywords('excludedKeywords', initialExcludedKeywords, setExcludedKeywords);
    }, []);

/*
    useEffect(() => {
        if (keywords.length === 0) {
            setKeywords(initialKeywords);
            localStorage.setItem('keywords', JSON.stringify(initialKeywords));
        } else {
            localStorage.setItem('keywords', JSON.stringify(keywords));
        }
    }, [keywords]);

    useEffect(() => {
        if (excludedKeywords.length === 0) {
            setExcludedKeywords(initialExcludedKeywords);
            localStorage.setItem('excludedKeywords', JSON.stringify(initialExcludedKeywords));
        } else {
            localStorage.setItem('excludedKeywords', JSON.stringify(excludedKeywords));
        }
    }, [excludedKeywords]);
*/

    return (
        <KeywordsContext.Provider value={{ keywords, setKeywords, excludedKeywords, setExcludedKeywords }}>
            {children}
        </KeywordsContext.Provider>
    );
};
